/**
 * ProCheckoutModal — Google sign-in → Razorpay (or dev mock) → cache Pro license JWT.
 */
import React, { useEffect, useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { apiConfigured, apiFetch } from '../utils/api';
import { saveAuthBundle, fetchPricing, getSessionToken } from '../utils/entitlement';
import { useModalA11y } from '../utils/useModalA11y';

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function ProCheckoutModal({ onClose, onActivated, source = 'settings' }) {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
  const [step, setStep] = useState('intro'); // intro | paying | done | error
  const [pricing, setPricing] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(() => getSessionToken());
  const [account, setAccount] = useState(null);
  const [askPush, setAskPush] = useState(false);

  useModalA11y({ onClose, isOpen: true });

  useEffect(() => {
    let cancelled = false;
    fetchPricing()
      .then((p) => { if (!cancelled) setPricing(p); })
      .catch(() => {
        if (!cancelled) setPricing({ amountInr: 499, early: true, label: 'Early bird lifetime' });
      });
    return () => { cancelled = true; };
  }, []);

  const priceLabel = pricing
    ? `₹${Math.round(pricing.amountInr)}${pricing.early ? ' early bird' : ''} lifetime`
    : 'Lifetime Pro';

  async function afterGoogle(credential) {
    setBusy(true);
    setError('');
    try {
      if (!apiConfigured()) {
        throw new Error('Pro checkout needs REACT_APP_API_URL. For local UI, set the API or use waitlist.');
      }
      const data = await apiFetch('/auth/google', {
        method: 'POST',
        body: { credential },
      });
      saveAuthBundle({ token: data.token, license: data.license, user: data.user });
      setSession(data.token);
      setAccount(data.user);
      if (data.pricing) setPricing(data.pricing);
      if (data.user?.plan === 'pro' && data.license) {
        setStep('done');
        setAskPush(true);
        onActivated?.({ user: data.user, license: data.license });
        return;
      }
      await startCheckout(data.token, data.user);
    } catch (e) {
      setError(e.message || 'Sign-in failed');
      setStep('error');
    } finally {
      setBusy(false);
    }
  }

  async function startCheckout(token, user) {
    setStep('paying');
    setBusy(true);
    try {
      const order = await apiFetch('/billing/create-order', { method: 'POST', token });
      if (order.alreadyPro && order.license) {
        saveAuthBundle({ license: order.license, user: { ...user, plan: 'pro' } });
        setStep('done');
        setAskPush(true);
        onActivated?.({ user: { ...user, plan: 'pro' }, license: order.license });
        return;
      }

      if (order.mock) {
        const confirmed = await apiFetch('/billing/confirm', {
          method: 'POST',
          token,
          body: {
            razorpay_order_id: order.orderId,
            razorpay_payment_id: `pay_dev_${Date.now()}`,
            razorpay_signature: 'dev',
            mock: true,
          },
        });
        saveAuthBundle({
          token: confirmed.token || token,
          license: confirmed.license,
          user: confirmed.user || { ...user, plan: 'pro' },
        });
        setStep('done');
        setAskPush(true);
        onActivated?.({ user: confirmed.user || { ...user, plan: 'pro' }, license: confirmed.license });
        return;
      }

      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) throw new Error('Could not load Razorpay Checkout');

      const key = order.keyId || process.env.REACT_APP_RAZORPAY_KEY_ID;
      await new Promise((resolve, reject) => {
        const rzp = new window.Razorpay({
          key,
          amount: order.amountPaise,
          currency: order.currency || 'INR',
          name: 'Spenderly',
          description: 'Lifetime Pro',
          order_id: order.orderId,
          prefill: { email: user?.email || '', name: user?.name || '' },
          theme: { color: '#0D0D0D' },
          handler: async (response) => {
            try {
              const confirmed = await apiFetch('/billing/confirm', {
                method: 'POST',
                token,
                body: {
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                },
              });
              saveAuthBundle({
                token: confirmed.token || token,
                license: confirmed.license,
                user: confirmed.user || { ...user, plan: 'pro' },
              });
              setStep('done');
              setAskPush(true);
              onActivated?.({ user: confirmed.user || { ...user, plan: 'pro' }, license: confirmed.license });
              resolve();
            } catch (err) {
              reject(err);
            }
          },
          modal: {
            ondismiss: () => reject(new Error('Payment cancelled')),
          },
        });
        rzp.on('payment.failed', (resp) => {
          reject(new Error(resp?.error?.description || 'Payment failed'));
        });
        rzp.open();
      });
    } catch (e) {
      setError(e.message || 'Checkout failed');
      setStep('error');
    } finally {
      setBusy(false);
    }
  }

  async function continuePay() {
    if (!session) {
      setError('Sign in with Google first');
      return;
    }
    await startCheckout(session, account);
  }

  return (
    <div className="sp-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="sp-modal waitlist-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pro-checkout-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="sp-modal-close" onClick={onClose} aria-label="Close">×</button>

        {step === 'done' ? (
          <div className="waitlist-success">
            <div className="waitlist-success-icon" aria-hidden="true">✓</div>
            <h3 id="pro-checkout-title">Pro is active</h3>
            <p>
              Full history and vault are unlocked on this device. Export your vault before switching phones.
            </p>
            {askPush && (
              <p className="mp-hint" style={{ marginTop: 12 }}>
                Optional: enable quiet reminders in Profile → Reminders (mid-month upload, goals). Not daily.
              </p>
            )}
            <button type="button" className="sp-btn-primary" onClick={onClose} style={{ marginTop: 16 }}>
              Done
            </button>
          </div>
        ) : (
          <div className="waitlist-form">
            <div className="waitlist-form-head">
              <div className="waitlist-pro-row">
                <span className="waitlist-pro-badge">PRO</span>
                <span className="waitlist-feature">{priceLabel}</span>
              </div>
              <h3 id="pro-checkout-title">Get Spenderly Pro</h3>
              <p>
                Pay once with Google. Activate online once; daily use stays on this device.
                Export vault before switching phones.
              </p>
            </div>

            {!apiConfigured() && (
              <p className="mp-hint" style={{ color: 'var(--danger)' }}>
                Payments API is not configured in this build. You can still join the waitlist from Profile.
              </p>
            )}

            {error && (
              <p className="mp-hint" style={{ color: 'var(--danger)' }} role="alert">{error}</p>
            )}

            {googleClientId && apiConfigured() && !session && (
              <div style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }}>
                <GoogleLogin
                  onSuccess={(res) => afterGoogle(res.credential)}
                  onError={() => { setError('Google sign-in failed'); setStep('error'); }}
                  useOneTap={false}
                  text="continue_with"
                  shape="rectangular"
                  theme="filled_black"
                />
              </div>
            )}

            {!googleClientId && apiConfigured() && (
              <p className="mp-hint">Set REACT_APP_GOOGLE_CLIENT_ID to enable Google checkout.</p>
            )}

            {session && step !== 'paying' && (
              <button
                type="button"
                className="sp-btn-primary"
                disabled={busy}
                onClick={continuePay}
              >
                {busy ? 'Working…' : `Pay ${priceLabel}`}
              </button>
            )}

            {step === 'paying' && busy && (
              <p className="mp-hint">Opening checkout…</p>
            )}

            <p className="waitlist-fineprint">
              By paying you agree to the{' '}
              <a href="/terms.html" target="_blank" rel="noreferrer">Terms</a>
              {' '}and{' '}
              <a href="/privacy.html" target="_blank" rel="noreferrer">Privacy Policy</a>.
              {' '}Source: {source}.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
